import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export type InvoiceStatus =
  | 'draft'
  | 'sent'
  | 'viewed'
  | 'partially_paid'
  | 'paid'
  | 'overdue'
  | 'void';

export type QuoteStatus = 'draft' | 'sent' | 'accepted' | 'expired' | 'declined';

export type PaymentMethod = 'bank_transfer' | 'cheque' | 'credit_card' | 'cash' | 'other';

export type TaxRate = 'hst_13' | 'gst_5' | 'exempt';

export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: TaxRate;
  /** Stored numeric tax percentage for the line (e.g. 13), independent of any tax-rate id. */
  taxRatePercent: number;
  taxRateId: string | null;
  discountType: 'flat' | 'percentage' | null;
  discountValue: number;
  discountAmount: number;
  amount: number;
  taxAmount: number;
}

export interface Invoice {
  id: string;
  tenantId: string;
  invoiceNumber: string;
  clientId: string;
  clientName: string;
  createdById: string | null;
  createdByName: string;
  billingEntityId: string | null;
  status: InvoiceStatus;
  isBadDebt: boolean;
  issueDate: string;
  dueDate: string;
  subtotal: number;
  taxTotal: number;
  total: number;
  outstandingBalance: number;
  notes: string | null;
  serviceTypeCode: string | null;
  jobId: string | null;
  discountDefinitionId: string | null;
  lineItems: InvoiceLineItem[];
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceDetail extends Invoice {
  payments: Payment[];
  taxBreakdown: TaxBreakdown[];
  sentAt: string | null;
  viewedAt: string | null;
  paidAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
}

export interface TaxBreakdown {
  taxRate: TaxRate;
  taxLabel: string;
  taxableAmount: number;
  taxAmount: number;
}

export interface Payment {
  id: string;
  invoiceId: string;
  amount: number;
  paymentMethod: PaymentMethod;
  paymentDate: string;
  referenceNumber: string | null;
  notes: string | null;
  recordedById: string;
  recordedByName: string;
  createdAt: string;
}

export interface Quote {
  id: string;
  tenantId: string;
  quoteNumber: string;
  clientId: string;
  clientName: string;
  createdByName: string;
  status: QuoteStatus;
  issueDate: string;
  expiryDate: string;
  subtotal: number;
  taxTotal: number;
  total: number;
  notes: string | null;
  lineItems: InvoiceLineItem[];
  acceptedAt: string | null;
  convertedInvoiceId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AgingBucket {
  label: string;
  minDays: number;
  maxDays: number | null;
  totalAmount: number;
  invoiceCount: number;
  clients: AgingClient[];
}

export interface AgingClient {
  clientId: string;
  clientName: string;
  amount: number;
  invoiceCount: number;
}

export interface AgingReportData {
  buckets: AgingBucket[];
  totalOutstanding: number;
  asOfDate: string;
}

// ─── Request Types ──────────────────────────────────────────────────────────

export interface InvoicesListParams {
  page?: number;
  pageSize?: number;
  status?: InvoiceStatus | '';
  clientId?: string;
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  dateFrom?: string;
  dateTo?: string;
  showAll?: boolean;
  badDebt?: boolean;
}

export interface InvoicesListResponse {
  invoices: Invoice[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreateInvoicePayload {
  clientId: string;
  issueDate: string;
  dueDate?: string;
  notes?: string;
  lineItems: CreateLineItemPayload[];
  serviceTypeCode?: string | null;
  jobId?: string | null;
}

export interface CreateLineItemPayload {
  description: string;
  quantity: number;
  unitPrice: number;
  taxRate: TaxRate;
  taxRateId?: string | null;
  /**
   * Stored tax percentage (e.g. 13). Sent only when the line has no `taxRateId`
   * so an existing rate (e.g. one carried over from a quote) is preserved
   * instead of being reset to 0%.
   */
  taxRatePercent?: number | null;
  discountType?: 'flat' | 'percentage' | null;
  discountValue?: number | null;
}

export interface UpdateInvoicePayload {
  clientId?: string;
  billingEntityId?: string | null;
  issueDate?: string;
  dueDate?: string;
  notes?: string;
  discountDefinitionId?: string;
  lineItems?: CreateLineItemPayload[];
}

export interface RecordPaymentPayload {
  invoiceId: string;
  amount: number;
  paymentMethod: PaymentMethod;
  paymentDate: string;
  referenceNumber?: string;
  notes?: string;
}

export interface QuotesListParams {
  page?: number;
  pageSize?: number;
  status?: QuoteStatus | '';
  clientId?: string;
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface QuotesListResponse {
  quotes: Quote[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreateQuotePayload {
  clientId: string;
  issueDate: string;
  expiryDate: string;
  notes?: string;
  lineItems: CreateLineItemPayload[];
}

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const billingKeys = {
  all: ['billing'] as const,
  invoices: () => [...billingKeys.all, 'invoices'] as const,
  invoiceLists: () => [...billingKeys.invoices(), 'list'] as const,
  invoiceList: (params: InvoicesListParams) => [...billingKeys.invoiceLists(), params] as const,
  invoiceDetails: () => [...billingKeys.invoices(), 'detail'] as const,
  invoiceDetail: (id: string) => [...billingKeys.invoiceDetails(), id] as const,
  quotes: () => [...billingKeys.all, 'quotes'] as const,
  quoteLists: () => [...billingKeys.quotes(), 'list'] as const,
  quoteList: (params: QuotesListParams) => [...billingKeys.quoteLists(), params] as const,
  quoteDetail: (id: string) => [...billingKeys.quotes(), 'detail', id] as const,
  aging: () => [...billingKeys.all, 'aging'] as const,
};

// ─── Invoice Hooks ──────────────────────────────────────────────────────────

/**
 * Fetch paginated invoices list with status filters and search.
 * Requirements: 10.1
 */
export function useInvoicesList(params: InvoicesListParams = {}) {
  return useQuery({
    queryKey: billingKeys.invoiceList(params),
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: Record<string, unknown>[];
        pagination?: { page: number; pageSize: number; total: number };
      }>('/billing/invoices', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 25,
          status: params.status || undefined,
          clientId: params.clientId || undefined,
          search: params.search || undefined,
          sortBy: params.sortBy || undefined,
          sortDirection: params.sortDirection || undefined,
          dateFrom: params.dateFrom || undefined,
          dateTo: params.dateTo || undefined,
          show_all: params.showAll ? 'true' : undefined,
          bad_debt: params.badDebt ? 'true' : undefined,
        },
      });

      const raw = response.data;
      const invoices: Invoice[] = (raw.data ?? []).map((inv) => ({
        id: (inv.id || '') as string,
        tenantId: (inv.tenant_id || inv.tenantId || '') as string,
        invoiceNumber: (inv.invoice_number || inv.invoiceNumber || '') as string,
        clientId: (inv.client_id || inv.clientId || '') as string,
        clientName: (inv.client_name || inv.clientName || '') as string,
        createdById: (inv.created_by_id ?? inv.createdById ?? null) as string | null,
        createdByName: (inv.created_by_name || inv.createdByName || '') as string,
        isBadDebt: Boolean(inv.is_bad_debt ?? inv.isBadDebt ?? false),
        billingEntityId: (inv.billing_entity_id ?? inv.billingEntityId ?? null) as string | null,
        status: (inv.status || 'draft') as InvoiceStatus,
        issueDate: (inv.issue_date || inv.issueDate || '') as string,
        dueDate: (inv.due_date || inv.dueDate || '') as string,
        subtotal: Number(inv.subtotal || 0),
        taxTotal: Number(inv.tax_amount || inv.taxTotal || 0),
        total: Number(inv.total_receivable || inv.totalReceivable || inv.total || 0),
        outstandingBalance: Number(inv.outstanding_balance || inv.outstandingBalance || 0),
        notes: (inv.notes ?? null) as string | null,
        serviceTypeCode: (inv.service_type_code ?? inv.serviceTypeCode ?? null) as string | null,
        jobId: (inv.job_id ?? inv.jobId ?? null) as string | null,
        discountDefinitionId: (inv.discount_definition_id ?? inv.discountDefinitionId ?? null) as
          | string
          | null,
        lineItems: [] as InvoiceLineItem[],
        createdAt: (inv.created_at || inv.createdAt || '') as string,
        updatedAt: (inv.updated_at || inv.updatedAt || '') as string,
      }));

      return {
        invoices,
        total: raw.pagination?.total ?? invoices.length,
        page: raw.pagination?.page ?? 1,
        pageSize: raw.pagination?.pageSize ?? 25,
      } as InvoicesListResponse;
    },
  });
}

/**
 * Fetch a single invoice with full detail (line items, payments, tax breakdown).
 * Requirements: 10.2
 */
export function useInvoiceDetail(invoiceId: string) {
  return useQuery({
    queryKey: billingKeys.invoiceDetail(invoiceId),
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: Record<string, unknown> }>(
        `/billing/invoices/${invoiceId}`,
      );
      const raw = response.data.data;
      if (!raw) return null;

      const lineItemsRaw = (raw.line_items || raw.lineItems || []) as Record<string, unknown>[];

      const invoice: InvoiceDetail = {
        id: (raw.id || '') as string,
        tenantId: (raw.tenant_id || raw.tenantId || '') as string,
        invoiceNumber: (raw.invoice_number || raw.invoiceNumber || '') as string,
        clientId: (raw.client_id || raw.clientId || '') as string,
        clientName: (raw.client_name || raw.clientName || '') as string,
        createdById: (raw.created_by_id ?? raw.createdById ?? null) as string | null,
        createdByName: (raw.created_by_name || raw.createdByName || '') as string,
        isBadDebt: Boolean(raw.is_bad_debt ?? raw.isBadDebt ?? false),
        billingEntityId: (raw.billing_entity_id ?? raw.billingEntityId ?? null) as string | null,
        status: (raw.status || 'draft') as InvoiceStatus,
        issueDate: (raw.issue_date || raw.issueDate || '') as string,
        dueDate: (raw.due_date || raw.dueDate || '') as string,
        subtotal: Number(raw.subtotal || 0),
        taxTotal: Number(raw.tax_amount || raw.taxTotal || 0),
        total: Number(raw.total || 0),
        outstandingBalance: Number(raw.outstanding_balance || raw.outstandingBalance || 0),
        notes: (raw.notes ?? null) as string | null,
        serviceTypeCode: (raw.service_type_code ?? raw.serviceTypeCode ?? null) as string | null,
        jobId: (raw.job_id ?? raw.jobId ?? null) as string | null,
        discountDefinitionId: (raw.discount_definition_id ?? raw.discountDefinitionId ?? null) as
          | string
          | null,
        lineItems: lineItemsRaw.map((li) => ({
          id: (li.id || '') as string,
          description: (li.description || '') as string,
          quantity: Number(li.quantity || 0),
          unitPrice: Number(li.unit_price ?? li.unitPrice ?? 0),
          taxRate: (li.tax_type || li.taxRate || 'exempt') as TaxRate,
          taxRatePercent: Number(li.tax_rate ?? li.taxRatePercent ?? 0),
          taxRateId: (li.tax_rate_id ?? li.taxRateId ?? null) as string | null,
          discountType: (li.discount_type ?? li.discountType ?? null) as
            | 'flat'
            | 'percentage'
            | null,
          discountValue: Number(li.discount_value ?? li.discountValue ?? 0),
          discountAmount: Number(li.discount_amount ?? li.discountAmount ?? 0),
          amount: Number(li.line_total ?? li.amount ?? 0),
          taxAmount: Number(li.tax_amount ?? li.taxAmount ?? 0),
        })),
        createdAt: (raw.created_at || raw.createdAt || '') as string,
        updatedAt: (raw.updated_at || raw.updatedAt || '') as string,
        payments: ((raw.payments || []) as Record<string, unknown>[]).map((p) => ({
          id: (p.id || '') as string,
          invoiceId: (p.invoice_id || p.invoiceId || invoiceId) as string,
          amount: Number(p.amount || 0),
          paymentMethod: (p.payment_method || p.paymentMethod || 'other') as PaymentMethod,
          referenceNumber: (p.reference_number || p.referenceNumber || null) as string | null,
          paymentDate: (p.payment_date || p.paymentDate || '') as string,
          recordedById: (p.recorded_by_id || p.recordedById || '') as string,
          recordedByName: (p.recorded_by_name || p.recordedByName || '') as string,
          notes: (p.notes ?? null) as string | null,
          createdAt: (p.created_at || p.createdAt || '') as string,
        })),
        taxBreakdown: (
          (raw.tax_breakdown || raw.taxBreakdown || []) as Record<string, unknown>[]
        ).map((t) => ({
          taxRate: (t.tax_type || t.taxRate || 'exempt') as TaxRate,
          taxLabel: (t.tax_label || t.taxLabel || t.tax_type || '') as string,
          taxableAmount: Number(t.taxable_amount ?? t.taxableAmount ?? 0),
          taxAmount: Number(t.tax_amount ?? t.taxAmount ?? 0),
        })),
        sentAt: (raw.sent_at ?? raw.sentAt ?? null) as string | null,
        viewedAt: (raw.viewed_at ?? raw.viewedAt ?? null) as string | null,
        paidAt: (raw.paid_at ?? raw.paidAt ?? null) as string | null,
        voidedAt: (raw.voided_at ?? raw.voidedAt ?? null) as string | null,
        voidReason: (raw.void_reason ?? raw.voidReason ?? null) as string | null,
      };

      // Attach extra fields not in the typed interface (for edit mode)
      (invoice as unknown as Record<string, unknown>).practiceEntityId = (raw.practice_entity_id ||
        raw.practiceEntityId ||
        '') as string;
      (invoice as unknown as Record<string, unknown>).jobReference = (raw.job_reference ||
        raw.jobReference ||
        null) as string | null;

      return invoice;
    },
    enabled: !!invoiceId,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/**
 * Create a new invoice.
 * Requirements: 10.1
 */
export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateInvoicePayload) => {
      // Transform camelCase payload to snake_case for the API
      const apiPayload = {
        client_id: payload.clientId,
        issue_date: payload.issueDate,
        due_date: payload.dueDate,
        notes: payload.notes,
        service_type_code: payload.serviceTypeCode,
        job_id: payload.jobId,
        line_items: payload.lineItems.map((li) => ({
          description: li.description,
          quantity: li.quantity,
          unit_price: li.unitPrice,
          tax_rate: li.taxRate === 'hst_13' ? 13 : li.taxRate === 'gst_5' ? 5 : 0,
          tax_rate_id: li.taxRateId || undefined,
          discount_type: li.discountType || undefined,
          discount_value: li.discountValue || undefined,
        })),
      };
      const response = await apiClient.post<ApiResponse<Invoice>>('/billing/invoices', apiPayload);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceLists() });
      queryClient.invalidateQueries({ queryKey: billingKeys.aging() });
    },
  });
}

/**
 * Update an existing invoice (only allowed in draft status).
 * Requirements: 10.2
 */
export function useUpdateInvoice(invoiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateInvoicePayload) => {
      // Transform camelCase payload to snake_case for the API
      const apiPayload: Record<string, unknown> = {};
      if (payload.clientId !== undefined) apiPayload.client_id = payload.clientId;
      if (payload.billingEntityId !== undefined)
        apiPayload.billing_entity_id = payload.billingEntityId;
      if (payload.issueDate !== undefined) apiPayload.issue_date = payload.issueDate;
      if (payload.dueDate !== undefined) apiPayload.due_date = payload.dueDate;
      if (payload.notes !== undefined) apiPayload.notes = payload.notes;
      if (payload.discountDefinitionId !== undefined)
        apiPayload.discount_definition_id = payload.discountDefinitionId || null;
      if (payload.lineItems) {
        apiPayload.line_items = payload.lineItems.map((li) => ({
          description: li.description,
          quantity: li.quantity,
          unit_price: li.unitPrice,
          tax_rate_id: li.taxRateId || undefined,
          // With no tax-rate id, keep the line's existing percentage (e.g. a
          // rate carried over from a quote) rather than resetting it to 0%.
          tax_rate: li.taxRateId
            ? undefined
            : typeof li.taxRatePercent === 'number' && li.taxRatePercent > 0
              ? li.taxRatePercent
              : li.taxRate === 'hst_13'
                ? 13
                : li.taxRate === 'gst_5'
                  ? 5
                  : 0,
          discount_type: li.discountType || undefined,
          discount_value: li.discountValue || undefined,
        }));
      }
      const response = await apiClient.put<ApiResponse<Invoice>>(
        `/billing/invoices/${invoiceId}`,
        apiPayload,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceDetail(invoiceId) });
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceLists() });
    },
  });
}

/**
 * Void an invoice.
 * Requirements: 10.12
 */
export function useVoidInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ invoiceId, reason }: { invoiceId: string; reason: string }) => {
      const response = await apiClient.patch<ApiResponse<Invoice>>(
        `/billing/invoices/${invoiceId}/status`,
        { status: 'void', void_reason: reason },
      );
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceDetail(variables.invoiceId) });
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceLists() });
      queryClient.invalidateQueries({ queryKey: billingKeys.aging() });
    },
  });
}

/**
 * Mark or unmark an invoice as bad debt. Only permitted server-side once the
 * invoice is sent and has an outstanding balance.
 */
export function useSetBadDebt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ invoiceId, isBadDebt }: { invoiceId: string; isBadDebt: boolean }) => {
      const response = await apiClient.patch<ApiResponse<Invoice>>(
        `/billing/invoices/${invoiceId}/bad-debt`,
        { is_bad_debt: isBadDebt },
      );
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceDetail(variables.invoiceId) });
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceLists() });
    },
  });
}

/**
 * Send an invoice to the client.
 */
export function useSendInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const response = await apiClient.post<ApiResponse<Invoice>>(
        `/billing/invoices/${invoiceId}/send`,
        {},
      );
      return response.data.data;
    },
    onSuccess: (_, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceDetail(invoiceId) });
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceLists() });
    },
  });
}

// ─── Payment Hooks ──────────────────────────────────────────────────────────

/**
 * Record a payment against an invoice.
 * Requirements: 10.18
 */
export function useRecordPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: RecordPaymentPayload) => {
      const response = await apiClient.post<ApiResponse<Payment>>(
        `/billing/invoices/${payload.invoiceId}/payments`,
        {
          amount: payload.amount,
          payment_method: payload.paymentMethod,
          payment_date: payload.paymentDate,
          reference_number: payload.referenceNumber || undefined,
          notes: payload.notes || undefined,
        },
      );
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceDetail(variables.invoiceId) });
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceLists() });
      queryClient.invalidateQueries({ queryKey: billingKeys.aging() });
      // Invalidate client ledger so receivable balance refreshes after payment
      queryClient.invalidateQueries({ queryKey: ['billing', 'client-ledger'] });
    },
  });
}

// ─── Quote Hooks ────────────────────────────────────────────────────────────

/**
 * Fetch paginated quotes list with status filters.
 */
export function useQuotesList(params: QuotesListParams = {}) {
  return useQuery({
    queryKey: billingKeys.quoteList(params),
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: Record<string, unknown>[];
        pagination?: { page: number; pageSize: number; total: number };
      }>('/billing/quotes', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 25,
          status: params.status || undefined,
          clientId: params.clientId || undefined,
          search: params.search || undefined,
          sortBy: params.sortBy || undefined,
          sortDirection: params.sortDirection || undefined,
        },
      });
      const raw = response.data;
      const quotes: Quote[] = (raw.data ?? []).map((q) => ({
        id: (q.id || '') as string,
        tenantId: (q.tenant_id || q.tenantId || '') as string,
        quoteNumber: (q.quote_number || q.quoteNumber || '') as string,
        clientId: (q.client_id || q.clientId || '') as string,
        clientName: (q.client_name || q.clientName || '') as string,
        createdByName: (q.created_by_name || q.createdByName || '') as string,
        status: (q.status || 'draft') as QuoteStatus,
        issueDate: (q.issue_date || q.issueDate || q.created_at || '') as string,
        expiryDate: (q.valid_until || q.expiryDate || '') as string,
        subtotal: Number(q.subtotal || 0),
        taxTotal: Number(q.tax_amount || q.taxTotal || 0),
        total: Number(q.total_receivable || q.totalReceivable || q.total || 0),
        notes: (q.notes ?? null) as string | null,
        lineItems: [] as InvoiceLineItem[],
        acceptedAt: (q.accepted_at ?? q.acceptedAt ?? null) as string | null,
        convertedInvoiceId: (q.converted_invoice_id ?? q.convertedInvoiceId ?? null) as
          | string
          | null,
        createdAt: (q.created_at || q.createdAt || '') as string,
        updatedAt: (q.updated_at || q.updatedAt || '') as string,
      }));
      return {
        quotes,
        total: raw.pagination?.total ?? quotes.length,
        page: raw.pagination?.page ?? 1,
        pageSize: raw.pagination?.pageSize ?? 25,
      } as QuotesListResponse;
    },
  });
}

/**
 * Create a new quote.
 */
export function useCreateQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateQuotePayload) => {
      const response = await apiClient.post<ApiResponse<Quote>>('/billing/quotes', payload);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteLists() });
    },
  });
}

/**
 * Accept a quote (marks it as accepted and optionally converts to invoice).
 */
export function useAcceptQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (quoteId: string) => {
      const response = await apiClient.post<ApiResponse<Quote>>(
        `/billing/quotes/${quoteId}/accept`,
      );
      return response.data.data;
    },
    onSuccess: (_, quoteId) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteDetail(quoteId) });
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteLists() });
    },
  });
}

/**
 * Convert an accepted quote to an invoice.
 */
export function useConvertQuoteToInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (quoteId: string) => {
      const response = await apiClient.post<ApiResponse<Invoice>>(
        `/billing/quotes/${quoteId}/convert`,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteLists() });
      queryClient.invalidateQueries({ queryKey: billingKeys.invoiceLists() });
    },
  });
}

/**
 * Update a quote's status (send, decline, expire).
 */
export function useUpdateQuoteStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ quoteId, status }: { quoteId: string; status: string }) => {
      const response = await apiClient.patch<ApiResponse<Quote>>(
        `/billing/quotes/${quoteId}/status`,
        { status },
      );
      return response.data.data;
    },
    onSuccess: (_, { quoteId }) => {
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteDetail(quoteId) });
      queryClient.invalidateQueries({ queryKey: billingKeys.quoteLists() });
    },
  });
}

// ─── Debit Note Types ───────────────────────────────────────────────────────

export type DebitNoteStatus = 'draft' | 'issued';

export interface DebitNote {
  id: string;
  tenantId: string;
  clientId: string;
  clientName: string;
  createdByName: string;
  amount: number;
  reason: string;
  status: DebitNoteStatus;
  transactionDate: string;
  createdAt: string;
  updatedAt: string;
}

export interface DebitNotesListParams {
  page?: number;
  pageSize?: number;
  status?: DebitNoteStatus | '';
  clientId?: string;
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface DebitNotesListResponse {
  debitNotes: DebitNote[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CreateDebitNotePayload {
  clientId: string;
  amount: number;
  reason: string;
  status: DebitNoteStatus;
}

// ─── Debit Note Query Keys ──────────────────────────────────────────────────

export const debitNoteKeys = {
  all: ['debit-notes'] as const,
  lists: () => [...debitNoteKeys.all, 'list'] as const,
  list: (params: DebitNotesListParams) => [...debitNoteKeys.lists(), params] as const,
  details: () => [...debitNoteKeys.all, 'detail'] as const,
  detail: (id: string) => [...debitNoteKeys.details(), id] as const,
};

// ─── Debit Note Hooks ───────────────────────────────────────────────────────

/**
 * Fetch paginated debit notes list with status filters and search.
 * Requirements: 15.19
 */
export function useDebitNotesList(params: DebitNotesListParams = {}) {
  return useQuery({
    queryKey: debitNoteKeys.list(params),
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: Record<string, unknown>[];
        pagination?: { page: number; pageSize: number; total: number };
      }>('/billing/debit-notes', {
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 25,
          status: params.status || undefined,
          clientId: params.clientId || undefined,
          search: params.search || undefined,
          sortBy: params.sortBy || undefined,
          sortDirection: params.sortDirection || undefined,
        },
      });
      const raw = response.data;
      const debitNotes: DebitNote[] = (raw.data ?? []).map((dn) => ({
        id: (dn.id || '') as string,
        tenantId: (dn.tenant_id || dn.tenantId || '') as string,
        clientId: (dn.client_id || dn.clientId || '') as string,
        clientName: (dn.client_name || dn.clientName || '') as string,
        createdByName: (dn.created_by_name || dn.createdByName || '') as string,
        amount: Number(dn.amount || 0),
        reason: (dn.reason || '') as string,
        status: (dn.status || 'draft') as DebitNoteStatus,
        transactionDate: (dn.transaction_date ||
          dn.transactionDate ||
          dn.created_at ||
          dn.createdAt ||
          '') as string,
        createdAt: (dn.created_at || dn.createdAt || '') as string,
        updatedAt: (dn.updated_at || dn.updatedAt || '') as string,
      }));
      return {
        debitNotes,
        total: raw.pagination?.total ?? debitNotes.length,
        page: raw.pagination?.page ?? 1,
        pageSize: raw.pagination?.pageSize ?? 25,
      } as DebitNotesListResponse;
    },
  });
}

/**
 * Create a new debit note.
 * Requirements: 15.19
 */
export function useCreateDebitNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateDebitNotePayload) => {
      const response = await apiClient.post<ApiResponse<DebitNote>>(
        '/billing/debit-notes',
        payload,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: debitNoteKeys.lists() });
    },
  });
}

// ─── Aging Report Hook ──────────────────────────────────────────────────────

/**
 * Fetch AR aging report data with bucket breakdown.
 * Requirements: 10.18
 */
export function useAgingReport() {
  return useQuery({
    queryKey: billingKeys.aging(),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<AgingReportData>>('/billing/reports/aging');
      return response.data.data;
    },
  });
}
